/**
 * Hold qty: units being held / short on an Rx ("holding 12 of 30" -> 12).
 * It belongs to the hold; it is not a stock count or a dispense instruction.
 *
 * The checks mirror the server rules so a bad value is caught before
 * submitting (the server rejects a whole batch on the first bad row).
 * A hold placed before qty existed comes back as null: "not recorded",
 * never zero, so it is shown as "—".
 */
export const HOLD_QTY_MAX = 99999.99;

// What the field may hold while typing: up to 5 whole digits, 2 decimals.
const TYPING_PATTERN = /^\d{0,5}(\.\d{0,2})?$/;
// A complete plain decimal: "12", "12.5", ".5" (not "12.", "1e3").
const PLAIN_DECIMAL = /^(\d+(\.\d+)?|\.\d+)$/;

export const isHoldQtyTyping = value =>
  TYPING_PATTERN.test(String(value ?? ''));

export const hasHoldQty = qty =>
  qty !== null && qty !== undefined && String(qty).trim() !== '';

// Returns the problem with a value, or '' when it is fine.
export const validateHoldQty = (value, { required = true } = {}) => {
  const text = hasHoldQty(value) ? String(value).trim() : '';
  if (text === '') return required ? 'Qty is required' : '';
  if (!PLAIN_DECIMAL.test(text)) return 'Qty must be a number, like 12 or 2.5';
  const num = Number(text);
  if (!(num > 0)) return 'Qty must be greater than 0';
  const decimals = text.includes('.') ? text.split('.')[1].length : 0;
  if (decimals > 2) return 'Qty can have at most 2 decimal places';
  if (num > HOLD_QTY_MAX) return 'Qty must be at most 99999.99';
  return '';
};

export const toHoldQty = value => Number(String(value).trim());

export const formatHoldQty = qty => {
  if (!hasHoldQty(qty)) return '—';
  const num = Number(qty);
  return Number.isFinite(num)
    ? num.toLocaleString('en-US', { maximumFractionDigits: 2 })
    : '—';
};

// Hold endpoints answer with message: ["..."]; some order endpoints nest it
// under output. Turn whichever is there into one line of text.
export const holdApiMessage = (payload, fallback = '') => {
  const message = payload?.message ?? payload?.output?.message;
  if (Array.isArray(message)) {
    return message.filter(Boolean).join(' ') || fallback;
  }
  return message ? String(message) : fallback;
};
